import React from "react";
import { SENSOR_CARLINE_SKILL_FOLDER_URL } from "constants/pipelines";
import type { LaunchableContentTabProps } from "components/Launchables/DeployRedesign/shared/content/tabs/LaunchableContentTab";
import {
  InlineLink,
  LinkedText,
  Paragraph,
  type PipelineFaq,
  PromptExample,
  RequirementsTable,
  SectionHeading,
} from "../shared";
import { PhysicalAISkin } from "../PhysicalAISkin";
const SENSOR_CARLINE_HEADER = {
  title: "Neural Reconstruction",
  summary:
    "Convert multi-sensor driving recordings into editable, photorealistic 3D scenes for simulation, training, and validation at scale, agent-driven from natural language prompts.",
  badges: [
    "NVIDIA Omniverse NuRec",
    "3D Gaussian Splatting Reconstruction & Rendering",
    "Autonomous Vehicles Training and Validation",
  ],
} as const;

const SENSOR_CARLINE_PIPELINE_FEATURES = [
  [
    "NuRec reconstruction engine",
    "Trains 3D Gaussian splats from multi-camera driving clips using proprietary data or the nvidia/PhysicalAI-Autonomous-Vehicles dataset on Hugging Face.",
  ],
  [
    "gsplat rendering",
    "Interactive 3DGS rendering compatible with Isaac Sim, AlpaSim, and CARLA via the open-source gsplat library, co-maintained by NVIDIA and the Nerfstudio team.",
  ],
  [
    "NuRec Harmonizer",
    "Non-temporal (1024x576) artifact removal, correcting lighting, shadows, and view-dependent inconsistencies across novel viewpoints.",
  ],
  [
    "OpenUSD output",
    "USDZ scenes integrate directly into the OpenUSD ecosystem for downstream compatibility with simulation in Omniverse, Isaac Sim, CARLA, AlpaSim, or any existing simulation stack.",
  ],
] as const;

const SENSOR_CARLINE_DETAILS_REQUIREMENTS = [
  [
    "GPU",
    <>
      Single NVIDIA GPU — L40S / A100 / H100 or RTX Pro 6000. See{" "}
      <a
        href="https://docs.nvidia.com/nurec/basics/hardware.html"
        target="_blank"
        rel="noreferrer"
        className="text-highlight hover:underline"
      >
        docs.nvidia.com/nurec/basics/hardware.html
      </a>
      . NVIDIA Driver 580+.
    </>,
  ],
  [
    "Time to first output",
    "~20 minutes from Brev launchable open to first reconstructed-and-rendered scene on a sample clip.",
  ],
  [
    "Powered by",
    "NVIDIA Omniverse NuRec 26.04 (reconstruction) + gsplat (rendering) + NuRec Harmonizer (artifact removal).",
  ],
  ["Domains", "Autonomous vehicles · Physical AI · Robotics simulation."],
  ["Supported Harness", "OpenClaw, Claude Code, Codex"],
  ["Supported Model", "Opus 4.7 or higher, GPT 5.5 or higher"],
] as const;

const SENSOR_CARLINE_FAQS: readonly PipelineFaq[] = [
  {
    question: "Do I need my own driving data to try this?",
    answer:
      "No. Two NVIDIA Hugging Face datasets serve as sample inputs (HF token + manual dataset acknowledgement required):",
    linkItems: [
      {
        label: "nvidia/PhysicalAI-Autonomous-Vehicles",
        href: "https://huggingface.co/datasets/nvidia/PhysicalAI-Autonomous-Vehicles",
        description:
          "raw fleet sensor data for the full reconstruction pipeline.",
      },
      {
        label: "nvidia/PhysicalAI-Autonomous-Vehicles-NuRec",
        href: "https://huggingface.co/datasets/nvidia/PhysicalAI-Autonomous-Vehicles-NuRec",
        description:
          "pre-reconstructed USDZ scenes for fast rendering / augmentation paths.",
      },
    ],
    example:
      "Render clip 01d503d4-449b-46fc-8d78-9085e70d3554 from the NuRec dataset with default settings, then run the harmonizer on the output.",
  },
  {
    question: "What sensor configurations are supported?",
    answer:
      "Multi-camera rigs with pinhole, fisheye, or F-Theta cameras. Rolling-shutter is handled natively. The pipeline expects pre-calibrated rig poses. SLAM is not part of this launchable's scope. Target sensor rigs are specified as a JSON sensor layout.",
  },
  {
    question: "Is the rendering backend open source?",
    answer:
      "Yes. NuRec renders 3D Gaussian Splatting scenes via gsplat, an open-source CUDA-accelerated rendering library co-maintained by NVIDIA and the Nerfstudio team. The rendering path is fully inspectable and extensible; reconstruction goes through NVIDIA Omniverse NuRec.",
    links: [["gsplat", "https://github.com/nerfstudio-project/gsplat"]],
  },
  {
    question:
      "Can I run the skills on my own infrastructure instead of the Brev launchable?",
    answer:
      "Yes. The underlying skills conform to the Anthropic Agent Skills Specification and are mirrored at github.com/NVIDIA/skills along with skills for other NVIDIA tools. Point Claude Code (Opus 4.7) or Codex CLI (GPT 5.5) at the repo and run on a supported GPU listed in docs.nvidia.com/nurec/basics/hardware. The Brev launchable is the no-setup alternative. If Brev hits capacity limits, fall back to the BYO path on local compute.",
    links: [
      ["github.com/NVIDIA/skills", "https://github.com/NVIDIA/skills"],
      [
        "docs.nvidia.com/nurec/basics/hardware",
        "https://docs.nvidia.com/nurec/basics/hardware.html",
      ],
    ],
  },
] as const;

export const SensorCarlineHeader: React.FC = () => (
  <div className="flex flex-col">
    <h1 className="text-white text-3xl font-semibold">
      {SENSOR_CARLINE_HEADER.title}
    </h1>
    <p className="text-secondary text-sm mt-2">
      {SENSOR_CARLINE_HEADER.summary}
    </p>
    <div className="flex items-center gap-2 flex-wrap mt-4">
      {SENSOR_CARLINE_HEADER.badges.map((badge) => (
        <span
          key={badge}
          className="text-xs text-white/70 border border-zinc-700 px-2.5 py-1 rounded-full"
        >
          {badge}
        </span>
      ))}
    </div>
  </div>
);

const SensorCarlineHeroPage: React.FC = () => (
  <div className="flex flex-col gap-6">
    <div>
      <Paragraph>
        Novel sensor data scenarios for autonomous driving simulation, training
        and validation, at scale.
      </Paragraph>
      <Paragraph>
        Driven by user prompts, an AI agent uses NVIDIA Omniverse NuRec to:
      </Paragraph>
      <ul className="list-disc list-inside text-sm text-white/85 flex flex-col gap-2 mt-3">
        <li>
          Convert multi-sensor (camera and LiDAR) driving clips into reusable 3D
          Gaussian Splatting scenes in USDZ format
        </li>
        <li>
          Edit reconstructed scenes by adjusting sensor poses, adding, removing,
          or extracting actors, and modifying dynamic actor trajectories
        </li>
        <li>Render modified scenes to generate new sensor outputs</li>
        <li>
          Harmonize output, correcting lighting and cleaning up artifacts, to
          produce high-quality sensor data for simulation, training, and
          validation
        </li>
      </ul>
    </div>

    <div className="aspect-video overflow-hidden rounded-md border border-zinc-800 bg-black">
      <iframe
        className="h-full w-full"
        src="https://www.youtube.com/embed/vhXnefuZ-wA"
        title="NVIDIA Omniverse NuRec demo"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    </div>

    <div>
      <SectionHeading>Agent Experience</SectionHeading>
      <Paragraph>
        Open the launchable on Brev and enter a natural-language goal to get
        started.
      </Paragraph>
      <p className="text-sm font-semibold text-white/90 mt-3">
        Example prompts:
      </p>
      <div className="flex flex-col gap-3 mt-2">
        <PromptExample>“Demo NuRec”</PromptExample>
        <PromptExample>
          “Get a reconstructed clip from{" "}
          <InlineLink href="https://huggingface.co/datasets/nvidia/PhysicalAI-Autonomous-Vehicles-NuRec">
            Hugging Face
          </InlineLink>{" "}
          and render it as if it was seen from a different sensor rig
          configuration. Translate the front camera +0.5 meters up. Show me a
          comparison between the original and the translated clip.”
        </PromptExample>
        <PromptExample>
          “Get raw sensor data from{" "}
          <InlineLink href="https://huggingface.co/datasets/nvidia/PhysicalAI-Autonomous-Vehicles">
            Hugging Face
          </InlineLink>{" "}
          and create a reconstructed USDZ scene. Show me renderings of each
          sensor after reconstruction, comparing them to the ground truth clips
          side by side.”
        </PromptExample>
        <PromptExample>
          “Harvest the 3D assets from a Physical AI dataset in{" "}
          <InlineLink href="https://huggingface.co/datasets/nvidia/PhysicalAI-Autonomous-Vehicles-NCore">
            Hugging Face
          </InlineLink>{" "}
          and give me the USD files and orbit renderings of each asset.”
        </PromptExample>
      </div>
      <p className="text-sm text-white/70 leading-relaxed mt-3">
        Observe any of the above workflows with OSMO (visualize background steps
        and logs):
      </p>
      <ul className="list-disc list-inside text-sm text-white/85 flex flex-col gap-1 mt-2">
        <li>
          <span className="italic">“Run with OSMO locally”</span>
        </li>
        <li>
          <span className="italic">“Run with OSMO at-scale in Azure”</span>
        </li>
      </ul>
      <p className="text-sm text-white/70 leading-relaxed mt-3">
        For example:{" "}
        <span className="italic">
          “Reconstruct 500 clips using OSMO at-scale in Azure”
        </span>
      </p>
      <p className="text-sm text-white/70 leading-relaxed mt-3">
        Scale any workflow with OSMO for background visualization and logs,
        locally or at scale on Azure.
      </p>
      <p className="text-sm font-semibold text-white/90 mt-3">The agent:</p>
      <ul className="list-disc list-inside text-sm text-white/85 flex flex-col gap-2 mt-2">
        <li>
          Ships with a NuRec demo shortcut that reconstructs a sample clip and
          produces results in 15 minutes, no local setup required
        </li>
        <li>
          Plans and executes reconstruction, rendering, and harmonization in
          sequence, with automatic fallback on failure
        </li>
        <li>
          Adjusts quality, resolution, sensor rig, and harmonization parameters
          on request
        </li>
        <li>Reports progress throughout; status is available on request</li>
      </ul>
    </div>

    <div>
      <SectionHeading>Pipeline Features</SectionHeading>
      <div className="grid grid-cols-1 gap-2">
        {SENSOR_CARLINE_PIPELINE_FEATURES.map(([feature, detail], i) => (
          <div
            key={feature}
            className="flex gap-3 items-start rounded-md border border-zinc-800 bg-zinc-900/40 px-4 py-3 transition-colors"
          >
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-zinc-700 text-[10px] font-bold text-white/60">
              {i + 1}
            </span>
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-semibold text-white/90">
                {feature}
              </span>
              <span className="text-xs text-white/55 leading-relaxed">
                {detail}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const SensorCarlineOverview: React.FC = () => (
  <div className="flex flex-col gap-5">
    <div>
      <Paragraph>
        Autonomous-vehicle and robotics development depend on diverse,
        edge-case-rich sensor data. Most fleet data is captured once, used for
        training, and archived. Neural Reconstruction turns single-use data into
        reusable 3D environments: a real multi-camera driving clip becomes a 3D
        Gaussian Splatting reconstruction in OpenUSD, renderable from arbitrary
        viewpoints, editable, and harmonizable. The task is photoreal scene
        reconstruction and interactive rendering at production fidelity, not
        point-cloud meshing.
      </Paragraph>
    </div>

    <div>
      <SectionHeading>What It Does</SectionHeading>
      <Paragraph>
        The pipeline ingests a multi-camera driving clip in NCore format and a
        target rendering spec (JSON sensor rig or trajectory). It uses NVIDIA
        Omniverse NuRec 26.04 to fit 3D Gaussian splats to the captured sensor
        data, renders the resulting scene interactively via the open-source
        gsplat library (which NVIDIA co-maintains), and runs NuRec Harmonizer to
        remove temporal artifacts and harmonize lighting and shadows. The output
        is a USDZ scene plus rendered video, ready for downstream simulation in
        Omniverse, Isaac Sim, CARLA, or AlpaSim.
      </Paragraph>
    </div>

    <div>
      <SectionHeading>Use Cases</SectionHeading>
      <ul className="list-disc list-inside text-sm text-white/85 flex flex-col gap-2">
        <li>
          <span className="font-semibold text-white/90">
            Novel-viewpoint rendering and sensor-rig adaptation:
          </span>{" "}
          re-render real driving data from any camera position, raised, lowered,
          shifted, or matched to a different vehicle&apos;s sensor rig, to
          expand training-set coverage without re-driving
        </li>
        <li>
          <span className="font-semibold text-white/90">
            Temporal harmonization and artifact cleanup:
          </span>{" "}
          smooth flicker, ghosting, lighting inconsistencies, and view-dependent
          artifacts on reconstructed scenes via NuRec Harmonizer
        </li>
        <li>
          <span className="font-semibold text-white/90">
            Synthetic agent insertion and scene editing:
          </span>{" "}
          insert new vehicles, pedestrians, or static objects into a
          reconstructed scene for closed-loop AV-stack validation
        </li>
        <li>
          <span className="font-semibold text-white/90">
            Fleet to OpenUSD digital twin:
          </span>{" "}
          convert customer driving logs into Omniverse-compatible reusable
          scenes for simulation handoff or external collaboration
        </li>
      </ul>
    </div>

    <div>
      <SectionHeading>Requirements</SectionHeading>
      <RequirementsTable rows={SENSOR_CARLINE_DETAILS_REQUIREMENTS} />
    </div>

    <div>
      <SectionHeading>Frequently Asked Questions</SectionHeading>
      <div className="flex flex-col gap-4">
        {SENSOR_CARLINE_FAQS.map(
          ({ question, answer, links, linkItems, example }) => (
            <div key={question} className="flex flex-col gap-1">
              <p className="text-sm font-semibold text-white/90">
                Q: {question}
              </p>
              <p className="text-sm text-white/70 leading-relaxed">
                A: {links ? <LinkedText text={answer} links={links} /> : answer}
              </p>
              {linkItems && (
                <ul className="list-disc list-outside pl-5 text-sm text-white/70 leading-relaxed flex flex-col gap-2 mt-1">
                  {linkItems.map(({ label, href, description }) => (
                    <li key={href}>
                      <InlineLink href={href}>{label}</InlineLink> —{" "}
                      {description}
                    </li>
                  ))}
                </ul>
              )}
              {example && (
                <p className="text-sm text-white/70 leading-relaxed mt-1">
                  Example: <span className="italic">{example}</span>
                </p>
              )}
            </div>
          ),
        )}
      </div>
    </div>
  </div>
);

export const SensorCarlineSkin: React.FC<LaunchableContentTabProps> = (
  props,
) => (
  <PhysicalAISkin
    {...props}
    skillFolderUrl={SENSOR_CARLINE_SKILL_FOLDER_URL}
    overview={<SensorCarlineHeroPage />}
    details={<SensorCarlineOverview />}
  />
);
